import {tokens} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled, {css} from 'styled-components'

import {Step} from '@/components/step'
import type {StepsFragment} from '@/graphql-types'
import Button from '@helios/shared/components/button'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import type {Props as RichTextProps} from '@helios/shared/components/rich-text'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Section, TitleLayout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

interface Props {
    title: string
    showCount: boolean
    items: StepsFragment['items']
    subtitle?: RichTextProps['content']
    ctaLink?: PrismicLinkProps
    ctaText?: string
    dataTestId?: string
}

export const Steps = React.forwardRef(
    ({title, subtitle, showCount, items, ctaLink, ctaText, dataTestId}: Props, ref) => {
        const numberOfColumns = items.length % 3 === 0 ? 3 : 2

        return (
            <StyledSection ref={ref} data-testid={dataTestId}>
                <TitleWrapper>
                    <Text variant="heading-large">{title}</Text>
                    {hasRichText(subtitle) && <RichText content={subtitle} color="muted" />}
                </TitleWrapper>

                <StepsWrapper $numberOfColumns={numberOfColumns}>
                    {items.map((item, index) => (
                        <li key={index}>
                            <StyledStep
                                heading={item.heading}
                                content={item.content}
                                backgroundColor={item.background_color}
                                image={item.image}
                                count={showCount ? index + 1 : undefined}
                            />
                        </li>
                    ))}
                </StepsWrapper>
                {ctaLink && ctaText && (
                    <StyledButton
                        forwardedAs={PrismicLink}
                        target="blank"
                        data-testid={`${dataTestId}-cta`}
                        {...ctaLink}
                    >
                        {ctaText}
                    </StyledButton>
                )}
            </StyledSection>
        )
    },
)

interface StepsWrapperProps {
    $numberOfColumns: number
}

export const StepsWrapperStyles = css`
    display: grid;
    grid-gap: ${tokens.spacing20};
    grid-auto-rows: 1fr;

    ${mq.min('sm')} {
        grid-gap: ${tokens.spacing40};
    }
`

const StepsWrapper = styled.ol<StepsWrapperProps>`
    ${StepsWrapperStyles}
    list-style: none;
    padding: 0;
    margin: 0;

    ${mq.min('lg')} {
        grid-template-columns: ${(props) => `repeat(${props.$numberOfColumns}, 1fr) `};
    }
`

const StyledStep = styled(Step)`
    height: 100%;
    align-content: space-between;
`

const StyledSection = styled(Section)`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        gap: ${tokens.spacing40};
    }

    ${mq.min('lg')} {
        gap: ${tokens.spacing60};
    }
`

const TitleWrapper = styled(TitleLayout)`
    display: grid;
    gap: ${tokens.spacing20};
`

const StyledButton = styled(Button)`
    ${mq.min('sm')} {
        justify-self: start;
    }
`

export const stepsFragment = graphql`
    fragment Steps on PrismicSlicePageDataBodySteps {
        __typename
        primary {
            title
            description {
                richText
            }
            show_count
            cta_link {
                ...PrismicLink
            }
            cta_text
        }
        items {
            heading
            content
            background_color
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(
                    breakpoints: $imageBreakpoints
                    sizes: "(max-width: 767px) calc(100vw - 48px), (max-width: 1200px) calc(100vw / 2 - 48px), (max-width: 1439px) calc(100vw / 2 - 120px), 600px"
                )
            }
        }
    }
`
